import type {
  AreaComponent,
  Placement,
  PlacementPosition,
  SizeMm,
  Surface,
  SurfacePoint,
} from '@repo/api-contracts';
import {
  type ComponentGrid,
  componentGrid,
  cylinderGridOnPhoto,
  type PhotoDimensions,
  type PhotoGridLines,
  type PhotoPolyline,
  type PhotoRect,
  shapeRingsOnPhoto,
  surfaceRectOnPhoto,
  surfaceScale,
} from '@/entities/edit-area';
import { closedPathOf } from '../model/svg-path';

interface Props {
  component: AreaComponent;
  surface: Surface;
  placement: Placement;
  photoSize: PhotoDimensions;
  spacingMm: number;
}

/** flat 배치 사각형 안의 격자선 — 축척 하나로 옮긴 두 점의 곧은 선이다. */
const straightGridLines = (
  rect: PhotoRect,
  scale: SurfacePoint,
  grid: Pick<ComponentGrid, 'vertical' | 'horizontal'>,
): PhotoGridLines => ({
  vertical: grid.vertical.map((x) => [
    { x: rect.x + x * scale.x, y: rect.y },
    { x: rect.x + x * scale.x, y: rect.y + rect.height },
  ]),
  horizontal: grid.horizontal.map((y) => [
    { x: rect.x, y: rect.y + y * scale.y },
    { x: rect.x + rect.width, y: rect.y + y * scale.y },
  ]),
});

/**
 * 표면 타입별 격자선. cylinder 는 곡면에 표본한 선(cylinderGridOnPhoto — 세로선은 곧고 가로선은 곡선),
 * flat 은 곧은 선이다. mesh 처럼 옮길 수 없으면 null 이다.
 */
const gridLinesOf = (
  surface: Surface,
  photo: PhotoDimensions,
  size: SizeMm,
  position: PlacementPosition,
  grid: Pick<ComponentGrid, 'vertical' | 'horizontal'>,
): PhotoGridLines | null => {
  if (surface.type === 'cylinder') return cylinderGridOnPhoto(surface, photo, size, position, grid);
  const rect = surfaceRectOnPhoto(surface, photo, size, position);
  const scale = surfaceScale(surface, photo);
  return rect === null || scale === null ? null : straightGridLines(rect, scale, grid);
};

/** 선의 양 끝. 두 점이면 곧은 선(line), 더 많으면 표본한 곡선(polyline)으로 그린다. */
const endpointsOf = (
  line: PhotoPolyline,
): { readonly first: SurfacePoint; readonly last: SurfacePoint } | null => {
  const first = line[0];
  const last = line[line.length - 1];
  return first === undefined || last === undefined ? null : { first, last };
};

export function ComponentGridOverlay({
  component,
  surface,
  placement,
  photoSize,
  spacingMm,
}: Props) {
  const grid = componentGrid(component, spacingMm);
  const lines = gridLinesOf(surface, photoSize, component, placement.position, grid);
  if (lines === null) return null;
  // 원형·다각형은 격자선을 모양 경로로 잘라 영역 안에만 보이게 한다. 사각형은 선이 이미 경계 안이라 자르지 않는다.
  const clipRings =
    component.shape === undefined
      ? null
      : shapeRingsOnPhoto(surface, photoSize, component, placement.position);
  const clipId = `grid-clip-${component.id}`;
  const strokes = [
    ...lines.vertical.map((line) => ({ axis: 'x', line })),
    ...lines.horizontal.map((line) => ({ axis: 'y', line })),
  ];
  return (
    <g pointerEvents="none" stroke="#059669" strokeWidth={0.5} opacity={0.7}>
      <title>Grid 간격 {grid.spacingMm} mm</title>
      {clipRings !== null && (
        <defs>
          <clipPath id={clipId}>
            <path d={clipRings.map(closedPathOf).join(' ')} clipRule="evenodd" />
          </clipPath>
        </defs>
      )}
      <g clipPath={clipRings === null ? undefined : `url(#${clipId})`}>
        {strokes.map(({ axis, line }) => {
          const ends = endpointsOf(line);
          if (ends === null) return null;
          const key = `${axis}-${ends.first.x},${ends.first.y}`;
          return line.length === 2 ? (
            <line
              key={key}
              x1={ends.first.x}
              x2={ends.last.x}
              y1={ends.first.y}
              y2={ends.last.y}
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <polyline
              key={key}
              points={line.map((point) => `${point.x},${point.y}`).join(' ')}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </g>
    </g>
  );
}
